import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const COOKIE_KEY = "mbs_cookie_consent";

function getCookieConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveCookieConsent(consent) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
  applyAnalyticsConsent(consent.analytics);
}

function applyAnalyticsConsent(enabled) {
  if (typeof window.gtag === "function") {
    window["ga-disable-G-SGKLLG8073"] = !enabled;
    if (enabled) {
      window.gtag("consent", "update", { analytics_storage: "granted" });
      window.gtag("event", "page_view");
    } else {
      window.gtag("consent", "update", { analytics_storage: "denied" });
    }
  }
}

export function useCookieConsent() {
  const consent = getCookieConsent();
  return {
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
    hasConsented: !!consent,
  };
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
    // Apply saved consent on load
    applyAnalyticsConsent(consent.analytics);
  }, []);

  const handleAcceptAll = () => {
    const consent = { essential: true, analytics: true, marketing: true };
    saveCookieConsent(consent);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { essential: true, analytics: false, marketing: false };
    saveCookieConsent(consent);
    setVisible(false);
  };

  const handleSavePrefs = () => {
    saveCookieConsent(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-3xl mx-auto bg-white border border-[#BFBFBF] rounded-2xl shadow-2xl shadow-black/10 overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-2 md:px-5 md:pt-5 md:pb-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Cookie className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#3F6B6B]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs md:text-sm font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Ce site utilise des cookies
                </h3>
                <p className="text-[10px] md:text-xs text-[#6B6B6B] mt-0.5 md:mt-1 leading-relaxed">
                  Nous utilisons des cookies essentiels et optionnels.{" "}
                  <Link to="/politique-cookies" className="text-[#3F6B6B] underline hover:text-[#2F4F4F]">
                    En savoir plus
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Details toggle */}
          <div className="px-4 md:px-5">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1.5 text-[10px] md:text-xs text-[#3F6B6B] font-medium hover:text-[#2F4F4F] transition-colors py-1"
              data-testid="cookie-toggle-details"
            >
              {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Personnaliser mes choix
            </button>

            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2.5 py-3">
                    {/* Essential */}
                    <label className="flex items-center justify-between p-3 rounded-lg bg-[#FBF9F5] border border-[#BFBFBF]">
                      <div>
                        <p className="text-xs font-semibold text-[#2F4F4F]">Cookies essentiels</p>
                        <p className="text-xs text-[#6B6B6B] mt-0.5">Nécessaires au fonctionnement du site (authentification, sécurité).</p>
                      </div>
                      <input type="checkbox" checked disabled className="w-4 h-4 rounded text-[#2F4F4F] border-[#BFBFBF]" />
                    </label>

                    {/* Analytics */}
                    <label className="flex items-center justify-between p-3 rounded-lg bg-[#FBF9F5] border border-[#BFBFBF] cursor-pointer hover:border-[#3F6B6B]/30 transition-colors" data-testid="cookie-analytics-toggle">
                      <div>
                        <p className="text-xs font-semibold text-[#2F4F4F]">Cookies analytiques</p>
                        <p className="text-xs text-[#6B6B6B] mt-0.5">Nous aident à comprendre comment vous utilisez le site pour l'améliorer.</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={prefs.analytics}
                        onChange={(e) => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                        className="w-4 h-4 rounded text-[#2F4F4F] border-[#BFBFBF] cursor-pointer"
                      />
                    </label>

                    {/* Marketing */}
                    <label className="flex items-center justify-between p-3 rounded-lg bg-[#FBF9F5] border border-[#BFBFBF] cursor-pointer hover:border-[#3F6B6B]/30 transition-colors" data-testid="cookie-marketing-toggle">
                      <div>
                        <p className="text-xs font-semibold text-[#2F4F4F]">Cookies marketing</p>
                        <p className="text-xs text-[#6B6B6B] mt-0.5">Permettent de vous proposer du contenu adapté à vos centres d'intérêt.</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={prefs.marketing}
                        onChange={(e) => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                        className="w-4 h-4 rounded text-[#2F4F4F] border-[#BFBFBF] cursor-pointer"
                      />
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Buttons */}
          <div className="px-4 pb-4 pt-1.5 md:px-5 md:pb-5 md:pt-2 flex flex-col sm:flex-row gap-2">
            {showDetails ? (
              <>
                <Button
                  onClick={handleSavePrefs}
                  className="flex-1 bg-[#2F4F4F] text-white hover:bg-[#1F3535] h-9 text-xs font-medium tracking-wide uppercase rounded-full"
                  data-testid="cookie-save-prefs"
                >
                  Enregistrer mes choix
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="flex-1 border-[#BFBFBF] text-[#2F4F4F] hover:bg-[#2F4F4F]/5 h-9 text-xs font-medium tracking-wide uppercase rounded-full"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-[#2F4F4F] text-white hover:bg-[#1F3535] h-9 text-xs font-medium tracking-wide uppercase rounded-full"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="flex-1 border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] hover:bg-[#2F4F4F]/5 h-9 text-xs font-medium tracking-wide uppercase rounded-full"
                  data-testid="cookie-reject-all"
                >
                  Refuser les optionnels
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
